import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getUserInfo, setUserInfo, clearUserInfo } from '../polyfills/userInfo';
import { getCookie, getCookieNames, getAliasFromPathname } from '../utils/cookieHelper';
import { head, isEmpty, filter } from 'lodash';
import { passwordProtected } from '../config/password-proctected';

const AUTH_EVENT_KEY = 'id_auth_event';
// Longest we hold the header/sign-up modal back while the user fetch is outstanding.
const AUTH_PENDING_TIMEOUT_MS = 8000;

const AuthContext = createContext(null);

/**
 * Central auth state manager shared across the Gatsby app.
 *
 * Responsibilities:
 *   • hydrate the cached user from sessionStorage on first load
 *   • expose helpers for updating auth state (loading/isCalled/user)
 *   • fan out auth changes across browser tabs via BroadcastChannel/localStorage
 *   • provide fetch + hardLogout utilities that keep sessionStorage and cookies in sync
 */
export const AuthProvider = ({ children }) => {
  const initialUser = typeof window !== 'undefined' ? getUserInfo() : null;

  // Canonical copy of the authenticated user. Null means we either never fetched
  // (public visitor) or the session has been cleared.
  const [user, setUser] = useState(initialUser);
  // Tracks whether a network request is in flight; shared via context so hooks
  // can render spinners without owning their own state machines.
  const [loading, setLoading] = useState(false);
  // Flag mirrors “have we completed at least one auth check?”. Helps templates
  // decide when it’s safe to render gated content or fall back to placeholders.
  const [isCalled, setIsCalled] = useState(Boolean(initialUser));
  // Track pathname to detect navigation changes and trigger refresh token check
  const [pathname, setPathname] = useState(
    typeof window !== 'undefined' ? window.location.pathname : ''
  );
  // Track fahousing cookie state to detect when it changes from false to true
  // This helps detect when user logs in via SSO in another application
  const cookieNames = getCookieNames();
  const [fahousingCookieState, setFahousingCookieState] = useState(
    typeof window !== 'undefined' ? getCookie(cookieNames.fahousing) : null
  );

  const isMounted = useRef(true);
  const inFlightRef = useRef(false);
  const bcRef = useRef(null);
  const refreshTokenCheckRanRef = useRef(false);
  const refreshTokenAbortControllerRef = useRef(null);
  const currentAliasRef = useRef(null);
  const cookieChangeInitialMountRef = useRef(true);
  // True from the moment a sign-out starts until the server has actually cleared the
  // cookies. The auth cookies are HttpOnly, so only oauth-signout's response can remove
  // them — and until it lands, every cookie-based check still reports a live session.
  // Without this, a re-mount or a poll during that window fetches the user straight back
  // in and writes them to sessionStorage, and the sign-out silently fails. ISS-1074.
  const signingOutRef = useRef(false);

  useEffect(() => () => {
    isMounted.current = false;
    try {
      bcRef.current?.close?.();
    } catch {}
  }, []);

  // Safe wrapper so downstream callers never attempt to set state on an unmounted
  // provider (a frequent pattern when sign-out triggers a redirect mid-request).
  const safeSet = useCallback((setter, value) => {
    if (isMounted.current) {
      setter(value);
    }
  }, []);

  // Update the current user in memory (and optionally persist to sessionStorage).
  // All callers (hooks, templates, background listeners) go through this helper
  // so we keep React state and sessionStorage in lockstep.
  const setUserState = useCallback(
    (nextUser, { persist = true, suppressFetch = false } = {}) => {
      if (persist) {
        if (nextUser) {
          setUserInfo(nextUser);
        } else {
          clearUserInfo();
        }
      }

      safeSet(setUser, nextUser || null);
      if (suppressFetch) {
        inFlightRef.current = false;
      }
    },
    [safeSet]
  );

  const setLoadingState = useCallback((value) => safeSet(setLoading, value), [safeSet]);
  const setIsCalledState = useCallback((value) => safeSet(setIsCalled, value), [safeSet]);

  // Lazily initialise the BroadcastChannel used for cross-tab sync.
  const ensureBroadcastChannel = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (bcRef.current) return;
    try {
      bcRef.current = new BroadcastChannel('id_auth');
    } catch {
      bcRef.current = null;
    }
  }, []);

  useEffect(() => {
    ensureBroadcastChannel();
  }, [ensureBroadcastChannel]);

  /**
   * Track pathname changes to detect navigation and trigger refresh token check.
   * 
   * Gatsby uses client-side routing, so we need to intercept history API calls
   * to detect when the user navigates between pages/aliases.
   * 
   * When pathname changes, the refresh token check effect will re-run and
   * validate tokens for the new alias if needed.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updatePathname = () => {
      setPathname(window.location.pathname);
    };

    // Set initial pathname
    updatePathname();

    // Intercept history API calls (pushState/replaceState) used by Gatsby router
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(...args) {
      originalPushState.apply(window.history, args);
      setTimeout(updatePathname, 0); // Update after pathname changes
    };

    window.history.replaceState = function(...args) {
      originalReplaceState.apply(window.history, args);
      setTimeout(updatePathname, 0); // Update after pathname changes
    };

    // Listen for browser back/forward buttons
    window.addEventListener('popstate', updatePathname);

    return () => {
      // Restore original functions on cleanup
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', updatePathname);
    };
  }, []);

  /**
   * Track fahousing cookie changes to detect when user logs in/out via SSO.
   * 
   * When fahousing changes from false/invalid to true/valid, we need to:
   * 1. Clear old user info from sessionStorage (in case user logged in as different user)
   * 2. Re-fetch user info to get the new user's data
   * 
   * This handles the scenarios:
   * - New tab: sessionStorage is empty, but cookie is valid (shared) → need to fetch user
   * - Existing tab: might have stale cached user from before user logged in as different user via SSO
   * - User logs in as normal user → redirected to unauthorized (Tab 1)
   * - User logs into another app as admin → fahousing stays true (no cookie value change)
   * - User goes back to Tab 1 → window focus validates user (clears and re-fetches to get admin user)
   * - User logs out → fahousing becomes false → clears user
   * - User logs in as admin → fahousing becomes true again → triggers re-fetch (even if user was null)
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkCookieChange = () => {
      // Performance: This function is lightweight and optimized:
      // - getCookie() is a simple string split operation (~microseconds)
      // - Boolean comparisons are very fast (~nanoseconds)
      // - Early return (line 219) prevents unnecessary state updates in 99% of cases
      // - Only updates React state when cookie actually changes (rare)
      // - React batches state updates, so no UI blocking
      const cookieNames = getCookieNames();
      const currentCookie = getCookie(cookieNames.fahousing);
      const currentIsValid = currentCookie && currentCookie !== 'false';
      const previousIsValid = fahousingCookieState && fahousingCookieState !== 'false';

      // On initial mount: If cookie is valid, always validate by clearing cached user (if any) to force re-fetch
      // This ensures we always have the correct current user, especially important when:
      // - New tab opens: sessionStorage is empty, but cookie is valid (shared across tabs)
      // - Existing tab: might have stale cached user from before user logged in as different user via SSO
      // Since cookies are shared across tabs but sessionStorage is per-tab, we need to validate
      // the user to ensure we have the correct current user (admin vs normal user)
      if (cookieChangeInitialMountRef.current) {
        cookieChangeInitialMountRef.current = false;
        if (currentIsValid) {
          // Always clear cached user (if any) on mount when cookie is valid to force fresh validation/fetch
          // This ensures we get the current user from the backend, not stale cached data
          // Handles both:
          // - New tab: sessionStorage is empty, but cookie is valid (shared) → need to fetch user
          // - Existing tab: might have stale cached user from before user logged in as different user via SSO
          // Clear any cached user to force re-fetch and ensure we have the correct current user
          clearUserInfo();
          setUserState(null, { persist: false });
          // Reset isCalled to trigger re-fetch in useAuthRedirect
          // useAuthRedirect will detect: hasCookie=true, cachedUser=null → calls fetchUser(alias)
          setIsCalledState(false);
          setLoadingState(false);
          // Update cookie state
          if (currentCookie !== fahousingCookieState) {
            setFahousingCookieState(currentCookie);
          }
          return;
        }
        // Update cookie state on initial mount even if cookie is invalid
        if (currentCookie !== fahousingCookieState) {
          setFahousingCookieState(currentCookie);
        }
      }

      // Only process if cookie state actually changed
      if (currentIsValid === previousIsValid && currentCookie === fahousingCookieState) {
        return; // No change, skip processing
      }

      // Cookie changed from invalid to valid (user logged in via SSO)
      // Clear old user info (if any) to force re-fetch with new user data
      // This handles both cases:
      // - User exists: clear it to get fresh data (might be different user)
      // - User is null: still need to trigger re-fetch (cookie became valid, need to fetch user)
      if (!previousIsValid && currentIsValid) {
        // Clear old user info from sessionStorage and React state (if any)
        clearUserInfo();
        setUserState(null, { persist: false });
        // Reset isCalled to trigger re-fetch
        // NOTE: fetchUser will be called by useAuthRedirect when it detects:
        // - hasCookie=true (cookie is valid)
        // - cachedUser=null (we just cleared it)
        // - !contextLoading && !inFlightRef.current (not already fetching)
        // This happens in useAuthRedirect.js line 89-95
        setIsCalledState(false);
        setLoadingState(false);
      }

      // Cookie changed from valid to invalid (user logged out via SSO)
      // Clean up user info immediately
      if (previousIsValid && !currentIsValid && user) {
        clearUserInfo();
        setUserState(null, { persist: false });
        setIsCalledState(true);
        setLoadingState(false);
      }

      // Update cookie state only if it changed
      if (currentCookie !== fahousingCookieState) {
        setFahousingCookieState(currentCookie);
      }
    };

    // Check cookie periodically to detect SSO changes from other apps
    // Runs every 7 seconds when visible, every 10 seconds when hidden
    // Checks immediately when tab becomes visible, then uses 7-second interval
    // This ensures immediate detection even when tab is in background while maintaining performance
    // 
    // Performance notes:
    // - Checks immediately when tab becomes visible (instant detection)
    // - Runs every 7 seconds when tab is visible (less frequent since we check immediately on visibility)
    // - Runs every 10 seconds when tab is hidden (less frequent to save resources, but still detects changes)
    // - checkCookieChange() is lightweight (~microseconds) and returns early if no change
    // - No DOM manipulation or heavy computations
    // - React state updates are batched and async, so no UI blocking
    let intervalId = null;
    const startInterval = (isVisible = true) => {
      if (intervalId) {
        // Clear existing interval before starting new one with different timing
        clearInterval(intervalId);
        intervalId = null;
      }
      const interval = isVisible ? 7000 : 10000; // 7s when visible, 10s when hidden
      intervalId = setInterval(checkCookieChange, interval);
    };
    const stopInterval = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Start interval based on current visibility state
    if (document.visibilityState === 'visible') {
      startInterval(true);
    } else {
      startInterval(false); // Still check when hidden, just less frequently
    }

    // Adjust interval timing when visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkCookieChange(); // Check immediately when tab becomes visible
        startInterval(true); // Switch to 7-second interval
      } else {
        startInterval(false); // Switch to 10-second interval when hidden
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check on focus (user might have switched tabs and logged in via SSO)
    // 
    // The 'focus' event fires when:
    // - User switches back to this browser tab from another tab
    // - User switches back to the browser from another application
    // - User clicks on the browser window to bring it to foreground
    // - Browser window is restored from minimized state
    // 
    // NOTE: The focus event does NOT fire on initial page load (that's handled by initial mount check)
    // 
    // When window regains focus, check cookie immediately and ensure interval is set correctly
    // based on visibility state. This ensures we detect changes quickly and use the right interval.
    const handleFocus = () => {
      // Skip focus check on initial mount (initial mount check handles it)
      // This prevents double-processing when focus fires during initial mount
      if (cookieChangeInitialMountRef.current) {
        return;
      }

      // Check cookie immediately when window regains focus
      // This will only process if cookie actually changed (early return in checkCookieChange)
      checkCookieChange();
      
      // Ensure interval is set correctly based on current visibility state
      // Focus and visibility can be independent, so we need to check visibility state
      const isVisible = document.visibilityState === 'visible';
      if (isVisible) {
        // If tab is visible, ensure we're using 7-second interval
        startInterval(true);
      } else {
        // If tab is hidden, ensure we're using 10-second interval
        startInterval(false);
      }
    };
    window.addEventListener('focus', handleFocus);

    // Check immediately on mount (handles new tab scenario)
    // This runs first, before focus handler can fire
    checkCookieChange();

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [fahousingCookieState, user, setUserState, setIsCalledState, setLoadingState]);

  // Broadcast auth events so other tabs update immediately (falls back to localStorage events).
  // This keeps multiple open dashboards synchronised when a user logs out in one tab.
  const broadcast = useCallback((type) => {
    try {
      bcRef.current?.postMessage?.({ type, ts: Date.now() });
    } catch {}

    try {
      localStorage.setItem(
        AUTH_EVENT_KEY,
        JSON.stringify({ type, ts: Date.now() })
      );
    } catch {}
  }, []);

  // Clears local auth state, cookies, and notifies other tabs / the backend logout endpoint.
  // Called when auth fails or the user explicitly signs out.
  const hardLogout = useCallback(
    async (alias) => {
      // Held for the whole sign-out, not just the local part. fetchUser refuses to run
      // while this is set: the cookies are still on the wire until oauth-signout answers,
      // so anything that asks "is there a user?" in the meantime gets "yes" and undoes
      // the sign-out we are in the middle of performing. ISS-1074.
      signingOutRef.current = true;
      try {
        clearUserInfo();
        // Note: clearAuthCookie() is not needed here because oauth-signout
        // already clears all cookies server-side (including HttpOnly cookies)
        broadcast('logout');

        safeSet(setUser, null);
        setIsCalledState(true);
        setLoadingState(false);

        if (typeof window !== 'undefined') {
          await fetch(`/.netlify/functions/oauth-signout${alias ? `?alias=${alias}` : ''}`, {
            method: 'GET',
            credentials: 'include',
            // The sign-out must survive the navigation that usually follows it. Without
            // keepalive the browser is free to abort the request when the page unloads,
            // and then the cookies are never cleared at all.
            keepalive: true,
          }).catch(() => {});
        }
      } catch {
      } finally {
        // The server has now cleared the cookies (or failed to, in which case retrying
        // on the next check is the right behaviour). Either way, stop suppressing.
        signingOutRef.current = false;
        clearUserInfo();
        safeSet(setUser, null);
      }
    },
    [broadcast, setIsCalledState, setLoadingState, safeSet]
  );

  // Fetches the authenticated user from the Netlify function, updating shared state on success.
  // Centralising here avoids duplicate requests when multiple templates load in parallel.
  const fetchUser = useCallback(
    async (alias) => {
      if (typeof window === 'undefined') return null;
      if (inFlightRef.current) return null;
      // A sign-out is in progress and the cookies have not been cleared yet, so this call
      // would report the user as still signed in and put them straight back. This is the
      // race behind "sometimes sign-out works, sometimes it doesn't". ISS-1074.
      if (signingOutRef.current) return null;

      inFlightRef.current = true;
      setLoadingState(true);

      try {
        const res = await fetch(`/.netlify/functions/oauth-get-user${alias ? `?alias=${encodeURIComponent(alias)}` : ''}`, {
          method: 'GET',
          credentials: 'include'          
        });      

        if (!res.ok) {
          await hardLogout(alias);
          return null;
        }

        const data = await res.json().catch(() => null);

        if (data && data.authenticated && data.user) {
          // User data is available - set it and return
          setUserState(data.user);
          broadcast('user-updated');
          setIsCalledState(true);
          return data.user;
        }

        // FusionAuth could not be reached, so we do not know whether the session is
        // still good. Leave it exactly as it is rather than tearing it down: hardLogout
        // revokes every application's refresh tokens for this user, so treating an
        // outage as a sign-out took the whole platform with it. We re-check on the next
        // call. ISS-1074.
        if (data && data.indeterminate) {
          return null;
        }

        await hardLogout(alias);
        return null;
      } catch {
        // Network failure reaching our own function - same reasoning as above. This is
        // not evidence the user signed out, so do not revoke anything.
        return null;
      } finally {
        inFlightRef.current = false;
        setLoadingState(false);
        setIsCalledState(true);
      }
    },
    [broadcast, hardLogout, setIsCalledState, setLoadingState, setUserState]
  );

  /**
   * Check refresh token validity for a protected alias.
   * 
   * This is called in the background after page load to silently validate
   * the refresh token without interrupting the user experience.
   * 
   * If the token is expired/invalid, the backend will return expired cookies
   * which will be cleared automatically. On the next page load, the user
   * will be redirected to sign in.
   * 
   * @param {string|null} targetAlias - The alias to check (null = use current pathname)
   */
  const checkRefreshToken = useCallback(async (targetAlias = null) => {
    if (typeof window === 'undefined') return;

    // Get current alias from pathname
    const currentAlias = getAliasFromPathname(window.location.pathname);
    const alias = targetAlias || currentAlias;

    // Early returns if conditions aren't met
    if (!alias) {
      return;
    }

    // If user navigated away from the target alias, cancel the check
    if (targetAlias && currentAlias !== targetAlias) {
      return;
    }

    // Only check protected aliases
    const isProtected = !isEmpty(head(filter(passwordProtected, ['lga', alias])));
    if (!isProtected) {
      return;
    }

    // Must have a valid user
    if (!user || isEmpty(user)) {
      return;
    }

    // Must have auth cookie
    const cookieNames = getCookieNames();
    const fahousingCookie = getCookie(cookieNames.fahousing);
    const hasCookie = fahousingCookie && fahousingCookie !== 'false';
    if (!hasCookie) {
      // Clean up session info and user state when cookie is missing or false
      clearUserInfo();
      setUserState(null, { persist: false }); // persist=false since we already cleared above
      return;
    }

    // Cancel any previous in-flight request
    if (refreshTokenAbortControllerRef.current) {
      refreshTokenAbortControllerRef.current.abort();
    }

    // Create new AbortController to allow canceling if user navigates away
    const abortController = new AbortController();
    refreshTokenAbortControllerRef.current = abortController;

    try {
      const res = await fetch(`/.netlify/functions/oauth-check-user?alias=${encodeURIComponent(alias)}`, {
        method: 'GET',
        credentials: 'include',
        signal: abortController.signal // Allow canceling request
      });

      // Check if request was canceled (user navigated away)
      if (abortController.signal.aborted) {
        return;
      }

      // If token is invalid, backend returns expired cookies
      // Clean up session info and user state since authentication has failed
      if (!res.ok) {
        clearUserInfo();
        setUserState(null, { persist: false }); // persist=false since we already cleared above
        return;
      }

      const data = await res.json().catch(() => null);

      // Check again after JSON parsing (user might have navigated)
      if (abortController.signal.aborted) {
        return;
      }

      if (data && data.authenticated) {
        // Refresh token is valid
      } else {
        // Backend returned unauthenticated, clean up session info and user state
        clearUserInfo();
        setUserState(null, { persist: false }); // persist=false since we already cleared above
      }
    } catch (error) {
      // Silently ignore abort errors (expected when user navigates away)
      if (error.name !== 'AbortError') {
        console.error('[AuthContext] Error checking refresh token:', error);
      }
    } finally {
      // Clean up abort controller if this was the active request
      if (refreshTokenAbortControllerRef.current === abortController) {
        refreshTokenAbortControllerRef.current = null;
      }
    }
  }, [user, setUserState]);

  /**
   * Schedule refresh token check to run once per protected alias after page load.
   * 
   * Flow:
   * 1. User visits a protected alias
   * 2. Wait for page to fully load
   * 3. Schedule background check (non-blocking)
   * 4. Check validates token silently
   * 
   * Key behaviors:
   * - Runs once per alias (won't re-run if user stays on same alias)
   * - Cancels if user navigates away before check completes
   * - Uses requestIdleCallback/setTimeout to avoid blocking UI
   * - Re-runs when pathname changes (navigation to different alias)
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Get current alias from pathname
    const alias = getAliasFromPathname(window.location.pathname);

    // Wait for user to be available before scheduling check
    if (!user || isEmpty(user)) {
      // Reset tracking when user becomes unavailable
      if (currentAliasRef.current !== null) {
        currentAliasRef.current = null;
        refreshTokenCheckRanRef.current = false;
      }
      return;
    }

    // If alias changed, cancel any in-flight check and reset tracking
    if (currentAliasRef.current !== null && currentAliasRef.current !== alias) {
      if (refreshTokenAbortControllerRef.current) {
        refreshTokenAbortControllerRef.current.abort();
        refreshTokenAbortControllerRef.current = null;
      }
      refreshTokenCheckRanRef.current = false;
    }

    // Update current alias
    currentAliasRef.current = alias;

    // Skip if no alias or already checked this alias
    if (!alias || refreshTokenCheckRanRef.current === alias) {
      return;
    }

    // Mark this alias as scheduled (prevents duplicate checks)
    refreshTokenCheckRanRef.current = alias;

    /**
     * Schedule the check to run in the background without blocking UI.
     * Uses requestIdleCallback when available, falls back to setTimeout.
     */
    const scheduleBackgroundCheck = () => {
      const scheduledAlias = alias; // Capture alias at scheduling time
      
      const executeCheck = () => {
        checkRefreshToken(scheduledAlias).catch((err) => {
          if (err.name !== 'AbortError') {
            console.error('[AuthContext] Background token check error:', err);
          }
        });
      };

      // Prefer requestIdleCallback for truly non-blocking execution
      if (window.requestIdleCallback) {
        const id = window.requestIdleCallback(executeCheck, { timeout: 2000 });
        return { type: 'idle', id };
      }

      // Fallback: setTimeout with delay to let page finish rendering
      const id = setTimeout(executeCheck, 1500);
      return { type: 'timeout', id };
    };

    let cleanupHandle = null;
    let loadHandler = null;

    // Schedule check based on page load state
    if (document.readyState === 'complete') {
      // Page already loaded, schedule immediately
      cleanupHandle = scheduleBackgroundCheck();
    } else {
      // Wait for page load event, then schedule
      loadHandler = () => {
        cleanupHandle = scheduleBackgroundCheck();
      };
      window.addEventListener('load', loadHandler, { once: true });
    }

    // Cleanup: cancel scheduled check and in-flight requests
    return () => {
      // Remove load event listener if added
      if (loadHandler) {
        window.removeEventListener('load', loadHandler);
      }
      
      // Cancel scheduled callback
      if (cleanupHandle) {
        if (cleanupHandle.type === 'idle' && window.cancelIdleCallback) {
          window.cancelIdleCallback(cleanupHandle.id);
        } else if (cleanupHandle.type === 'timeout') {
          clearTimeout(cleanupHandle.id);
        }
      }
      
      // Cancel in-flight fetch request (user navigated away)
      if (refreshTokenAbortControllerRef.current) {
        refreshTokenAbortControllerRef.current.abort();
        refreshTokenAbortControllerRef.current = null;
      }
    };
  }, [user, checkRefreshToken, pathname]);

  // Keep this tab in sync with auth events triggered elsewhere (BroadcastChannel + localStorage).
  // When another tab logs out or refreshes user data we instantly mirror the change here.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const channel = bcRef.current;

    const syncUserState = () => {
      const latest = getUserInfo();
      setUserState(latest, { persist: false });
      setIsCalledState(true);
    };

    const onMessage = (event) => {
      const type = event?.data?.type;
      if (type === 'logout') {
        clearUserInfo();
        setUserState(null, { persist: false });
        setIsCalledState(true);
        setLoadingState(false);
      } else if (type === 'user-updated') {
        syncUserState();
      }
    };

    channel?.addEventListener?.('message', onMessage);

    const onStorage = (e) => {
      if (e.key !== AUTH_EVENT_KEY || !e.newValue) return;
      try {
        const { type } = JSON.parse(e.newValue);
        if (type === 'logout') {
          clearUserInfo();
          setUserState(null, { persist: false });
          setIsCalledState(true);
          setLoadingState(false);
        } else if (type === 'user-updated') {
          syncUserState();
        }
      } catch {}
    };

    window.addEventListener('storage', onStorage);

    return () => {
      channel?.removeEventListener?.('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, [setIsCalledState, setLoadingState, setUserState]);
 
  // Whether we can say yet if this visitor is signed in. Arriving from another .id tool
  // the shared cookie is already set but the user has not been fetched, and treating that
  // gap as "signed out" flashed the Log in button and let a click open the sign-up modal
  // for someone who was in fact signed in.
  //
  // Pending until the provider has mounted (so the first client render matches the static
  // HTML), and then for as long as there is a session cookie with no user and a fetch
  // outstanding. Capped by a timeout so a page that never fetches — or a hung request —
  // cannot leave the header empty forever.
  const [hasMounted, setHasMounted] = useState(false);
  const [pendingTimedOut, setPendingTimedOut] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const liveCookie = hasMounted ? getCookie(getCookieNames().fahousing) : null;
  const hasSessionCookie = Boolean(liveCookie) && liveCookie !== 'false';
  const awaitingUser = hasSessionCookie && !user && (loading || !isCalled);
  const authPending = !hasMounted || (awaitingUser && !pendingTimedOut);

  useEffect(() => {
    if (!awaitingUser) {
      setPendingTimedOut(false);
      return undefined;
    }
    const id = setTimeout(() => safeSet(setPendingTimedOut, true), AUTH_PENDING_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [awaitingUser, safeSet]);

  const value = useMemo(
    () => ({
      user,
      loading,
      isCalled,
      authPending,
      setLoading: setLoadingState,
      setIsCalled: setIsCalledState,
      setUserState,
      fetchUser,
      hardLogout,
    }),
    [user, loading, isCalled, authPending, setLoadingState, setIsCalledState, setUserState, fetchUser, hardLogout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  // Convenience hook used by auth-aware components; ensures we only access the context
  // when wrapped in <AuthProvider>. Throws early in development if the provider is missing.
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

